import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";
import BaseDatePicker from "../../Base/BaseDatePicker";
import useServiceEmailsService from "../../../services/useServiceEmailsService";
import showToast from "../../../utils/toast";
import "./styles/ServicesPlanillaExportModal.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  defaultStartDate?: string;
  defaultEndDate?: string;
  extraFilters?: {
    search?: string;
    status?: string;
    request_date?: string;
    company_id?: string;
  };
};

type FormState = {
  start_date: string;
  end_date: string;
};

const EmailsSimpleExportModal: React.FC<Props> = ({
  isOpen,
  onClose,
  defaultStartDate = "",
  defaultEndDate = "",
  extraFilters,
}) => {
  const { downloadSimpleExcel, fetching } = useServiceEmailsService();
  const [form, setForm] = useState<FormState>({
    start_date: defaultStartDate,
    end_date: defaultEndDate || defaultStartDate,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      start_date: defaultStartDate,
      end_date: defaultEndDate || defaultStartDate,
    });
    setError("");
  }, [isOpen, defaultStartDate, defaultEndDate]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleChange = (name: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleDownload = async () => {
    if (!form.start_date || !form.end_date) {
      setError("Complete el rango de fechas");
      return;
    }
    if (new Date(form.start_date) > new Date(form.end_date)) {
      setError("La fecha de inicio debe ser anterior o igual a la de fin");
      return;
    }

    await downloadSimpleExcel(
      {
        start_date: form.start_date,
        end_date: form.end_date,
        search: extraFilters?.search || undefined,
        status: extraFilters?.status || undefined,
        request_date: extraFilters?.request_date || undefined,
        company_id: extraFilters?.company_id || undefined,
      },
      {
        onSuccess: () => {
          showToast("Excel de correos descargado (una hoja por día)", "success");
          onClose();
        },
        onError: (err) => {
          setError(err.message || "Error al descargar el Excel");
        },
      }
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="services-planilla-modal-overlay" onClick={onClose}>
      <div
        className="services-planilla-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="emails-simple-export-title"
      >
        <header className="services-planilla-modal__header">
          <div>
            <h2 id="emails-simple-export-title">Descargar correos Excel</h2>
            <p>
              Elige un rango de fechas. Se genera un archivo con{" "}
              <strong>una hoja por cada día</strong> (formato vista simple).
            </p>
          </div>
          <button
            type="button"
            className="services-planilla-modal__close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </header>

        <div className="services-planilla-modal__body">
          <div className="services-planilla-modal__dates">
            <div className="services-planilla-modal__field">
              <BaseDatePicker
                label="Fecha de inicio"
                name="start_date"
                value={form.start_date}
                onChange={(e) => handleChange("start_date", e.target.value)}
              />
            </div>
            <div className="services-planilla-modal__field">
              <BaseDatePicker
                label="Fecha de fin"
                name="end_date"
                value={form.end_date}
                onChange={(e) => handleChange("end_date", e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="services-planilla-modal__error" role="alert">
              {error}
            </div>
          )}
        </div>

        <footer className="services-planilla-modal__footer">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-download"
            onClick={() => void handleDownload()}
            disabled={Boolean(fetching.downloadSimple)}
          >
            <Download size={16} />
            {fetching.downloadSimple ? "Descargando..." : "Descargar Excel"}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default EmailsSimpleExportModal;
